/* eslint-disable i18next/no-literal-string */
import {
  Button,
  Icon,
  Image,
  OsStack,
  Paper,
  Typography,
} from "@outschool/backpack";
import { borderRadius } from "@outschool/backpack/dist-cjs/theme/borderRadius";
import {
  faChevronLeft,
  faDownload,
  faRepeat,
  faSparkles,
  faUser,
} from "@outschool/icons";
import { TrackedButton } from "@outschool/ui-components-shared";
import { useIsMobile } from "@outschool/ui-utils";
import React, { useState } from "react";

import { getCurrentLearner } from "../../providers/CurrentLearnerProvider";
import { useSaveAvatarMutation } from "./hooks";

interface AvatarResultProps {
  avatar: {
    name: string;
    description: string;
    imageUrl: string;
  };
  animals: string[];
  onBack: () => void;
}

export default function AvatarResult({
  avatar,
  animals,
  onBack,
}: AvatarResultProps) {
  const isMobile = useIsMobile();
  const imageWidth = isMobile ? 300 : 500;
  const [isSaved, setIsSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveAvatar, { loading: isSaving }] = useSaveAvatarMutation();

  const handleSave = async () => {
    setSaveError(null);
    try {
      await saveAvatar({
        variables: {
          imageCdnUrl: avatar.imageUrl,
          name: avatar.name,
          description: avatar.description,
          animals,
        },
        refetchQueries: [getCurrentLearner],
      });
      setIsSaved(true);
    } catch (err: any) {
      setSaveError(err.message || "Failed to save avatar");
    }
  };

  const handleDownload = () => {
    // Open image in a new tab so user doesn't lose current page
    window.open(avatar.imageUrl, "_blank");
  };

  return (
    <OsStack spacing={{ xs: 2, md: 4 }}>
      <Button
        startIcon={<Icon icon={faChevronLeft} />}
        size="small"
        sx={{ width: "fit-content" }}
        onClick={onBack}
      >
        Back
      </Button>
      <Paper
        variant="elevation"
        sx={{
          borderRadius: borderRadius.medium,
          padding: { xs: "16px", md: "32px" },
        }}
      >
        <OsStack
          spacing={{ xs: 4, md: 6 }}
          direction="column"
          alignItems="center"
        >
          <OsStack spacing={{ xs: 2, md: 4 }} alignItems="center">
            <Typography variant="h1">
              <Icon icon={faSparkles} color="warning" /> {avatar.name}{" "}
              <Icon icon={faSparkles} color="warning" />
            </Typography>
            <Typography variant="body1">
              Your new creature is ready! {avatar.description}
            </Typography>
          </OsStack>

          <Image
            src={avatar.imageUrl}
            alt={avatar.name}
            width={imageWidth}
            height={imageWidth}
            sx={{
              borderRadius: borderRadius.medium,
              objectFit: "contain",
            }}
          />

          {saveError && (
            <Typography variant="body2" color="error">
              {saveError}
            </Typography>
          )}
          {isSaved && (
            <Typography variant="body2" color="success.main">
              Avatar saved successfully!
            </Typography>
          )}
          <OsStack
            spacing={{ xs: 2, md: 4 }}
            direction={isMobile ? "column" : "row"}
          >
            <TrackedButton
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={isSaving || isSaved}
              startIcon={<Icon icon={faUser} />}
              trackingName="avatar_builder.set_as_profile_picture_button_clicked"
              trackingSharedProperties={{
                animals: animals,
                animalCount: animals.length,
              }}
            >
              {isSaving
                ? "Saving..."
                : isSaved
                ? "Saved!"
                : "Set as Profile Picture"}
            </TrackedButton>
            <Button
              variant="outlined"
              onClick={handleDownload}
              startIcon={<Icon icon={faDownload} />}
            >
              Download
            </Button>
            <Button
              variant="outlined"
              onClick={onBack}
              startIcon={<Icon icon={faRepeat} />}
            >
              Mix Again
            </Button>
          </OsStack>
        </OsStack>
      </Paper>
    </OsStack>
  );
}
